import React from "react";
import Card from "./Card";
import { formatLocalDate } from "./socFreshness";
import { FOCUS_RING } from "./socUi";
import { periodChange } from "./socConsole";

const KIND_HINT = {
  new: "First raised in the period",
  openedResolved: "Opened minus resolved in the period",
  events: "Events in the period",
  change: "Change between the first and last snapshot",
};

// "What moved" (/soc): what changed per area over the trend window, one compact bar row per area.
// The bar is the size of the move relative to the largest one shown (amber up, green down); each
// row still states its own measure, so areas with different units are never read as one scale.
// `trends` is undefined while loading, null when it failed; the window shown is the loaded
// response's own, so the label never disagrees with the numbers while a new range loads. Rows with
// a queue area filter the queue; rows with a view open that area's view (`onOpenView(viewKey)`).
const SocWhatMoved = ({ items, trends, selectedArea, onSelectArea, onOpenView = null }) => {
  let content;
  if (trends === undefined) {
    content = <p className="text-sm text-content-muted" data-testid="soc-moved-loading">Loading changes…</p>;
  } else if (trends === null) {
    content = <p className="text-sm text-content-secondary">Changes over the period couldn't be loaded.</p>;
  } else {
    const max = Math.max(1, ...items.filter((m) => !m.stale).map((m) => Math.abs(Number(m.value) || 0)));
    content = (
      <ul className="flex flex-col gap-0.5" data-testid="soc-moved-bars">
        {items.map((m) => {
          const selectable = !!m.queueArea && !m.stale;
          const selected = selectable && selectedArea === m.queueArea;
          // A stale area still opens: its view says when the data last came in.
          const opens = !selectable && !!m.view && !!onOpenView;
          const value = Number(m.value) || 0;
          // Every measure here is worse when it goes up (more raised, opened or events).
          const change = m.stale ? null : periodChange(value, m.previousValue);
          const width = value === 0 ? 0 : Math.max(2, (Math.abs(value) / max) * 100);
          const body = (
            <>
              <span className="w-28 shrink-0 truncate text-xs font-medium text-content-secondary">{m.label}</span>
              {m.stale ? (
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-status-warning">
                  No new data since {formatLocalDate(m.lastSync)}
                </span>
              ) : (
                <>
                  <span className="relative h-2 min-w-[3rem] flex-1 rounded-full bg-theme-secondary" aria-hidden="true">
                    <span
                      className={`absolute inset-y-0 left-0 rounded-full ${value > 0 ? "bg-status-warning" : "bg-status-success"}`}
                      style={{ width: `${width}%` }}
                    />
                  </span>
                  <span className="w-10 shrink-0 text-right text-sm font-bold text-content-primary tabular-nums">{m.headline}</span>
                  <span
                    className={`w-10 shrink-0 text-right text-[11px] font-semibold tabular-nums ${
                      !change || change.delta === 0 ? "text-content-muted" : change.delta > 0 ? "text-status-error" : "text-status-success"
                    }`}
                    data-testid={`soc-moved-change-${m.key}`}
                  >
                    {change ? (change.delta === 0 ? "=" : `${change.delta > 0 ? "▲" : "▼"}${Math.abs(change.delta)}`) : ""}
                  </span>
                  <span className="w-40 shrink-0 truncate text-xs text-content-muted">{m.detail}</span>
                </>
              )}
            </>
          );
          const vsPrevious = change ? ` Previous ${trends?.days ?? ""} days: ${m.previousValue}.` : "";
          const tip = `${m.label}: ${m.stale ? "no new data" : m.detail} · ${m.source}. ${KIND_HINT[m.kind]}.${vsPrevious}`;
          const className = `group flex w-full min-w-0 items-center gap-3 rounded-md px-2 py-1 text-left ${
            selected ? "bg-accent-cyan-muted ring-1 ring-accent-cyan" : ""
          }${selectable || opens ? ` transition-colors duration-150 hover:bg-theme-secondary ${FOCUS_RING}` : ""}`;
          return (
            <li key={m.key}>
              {selectable ? (
                <button
                  type="button"
                  aria-pressed={selected}
                  title={`${tip} Select to filter the incident queue.`}
                  onClick={() => onSelectArea(selected ? null : m.queueArea)}
                  className={className}
                  data-testid={`soc-moved-${m.key}`}
                >
                  {body}
                </button>
              ) : opens ? (
                <button
                  type="button"
                  title={`${tip} Select to open ${m.label}.`}
                  onClick={() => onOpenView(m.view)}
                  className={className}
                  data-testid={`soc-moved-${m.key}`}
                >
                  {body}
                </button>
              ) : (
                <div className={className} title={tip} data-testid={`soc-moved-${m.key}`}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const selectedLabel = selectedArea && items.find((m) => m.queueArea === selectedArea)?.label;
  return (
    <Card className="h-full">
      <section aria-label="What moved" className="min-w-0">
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-content-primary">
            What moved{trends?.days ? ` · last ${trends.days} days` : ""}
          </h2>
          <p className="text-xs text-content-muted">
            {selectedLabel
              ? `Filtering the queue to ${selectedLabel} · select it again to clear.`
              : "From source timestamps. Select an area to open its view. Control alerts are reviewed in GRC."}
          </p>
        </div>
        {content}
      </section>
    </Card>
  );
};

export default SocWhatMoved;
